import React, { useState, useEffect, useMemo } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Form, FormGroup, Label, Input, FormFeedback, Button, Row, Col } from 'reactstrap';
import { fetchTableData } from '../../../../Api/ApiMethods';
import { shouldToastError } from '../../../../Api/utils';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { normalizeDecimal, normalizeFieldValue, parseCurrencyToFloat, validatePositiveInteger, displayDecimal } from '../../../../_helper/regexHandler';
import { useModalCrudState } from '../../../../_helper/hooks/useModalCrudState';
import FilesComponent from '../../CallForms/Component/FilesComponent';

const toLocalISOString = (date) => {
    if (!date) return '';
    try {
        const dateObj = new Date(date);
        if (isNaN(dateObj.getTime())) {
            return '';
        }
        const tzOffset = dateObj.getTimezoneOffset() * 60000;
        const localDate = new Date(dateObj.getTime() - tzOffset);
        return localDate.toISOString().slice(0, 16);
    } catch {
        return '';
    }
};

const formatCurrency = (value) => {
    if (value === null || value === undefined) return '';
    return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(value);
}

const ExpenseDataModal = ({ apiEndpoint, isOpen, toggle, mode, selectedExpense, refreshData, customerId, autoAssignCustomer = false, accidentFile = [], callformId, readOnlyView = false, initialCustomerContractRate = null, initialIsMiddlemanContractBased = false, initialMiddlemanContractRate = null, initialMiddlemanContractPrice = null, relatedArbitrationSchema = [], arbitrationSchemaType = null, defaultAmount = null }) => {
    const initialExpenseData = useMemo(() => ({
        expenseNumber: '',
        amount: defaultAmount != null ? String(defaultAmount) : '',
        associatedBank: null,
        date: '',
        paymentStatus: 'Ödendi',
        invoiceNumber: '',
        description: '',
        accidentFile: accidentFile ? accidentFile.map(id => String(id)) : [],
        customer: autoAssignCustomer && customerId ? [String(customerId)] : [],
        callForm: callformId ? [String(callformId)] : [],
        customerContractRate: initialCustomerContractRate !== null ? initialCustomerContractRate : null,
        isMiddlemanContractBased: initialIsMiddlemanContractBased,
        middlemanContractRate: initialIsMiddlemanContractBased && initialMiddlemanContractRate !== null
            ? initialMiddlemanContractRate
            : null,
        middlemanContractPrice: !initialIsMiddlemanContractBased && initialMiddlemanContractPrice !== null
            ? initialMiddlemanContractPrice
            : null,
        relatedArbitrationSchema: relatedArbitrationSchema ? relatedArbitrationSchema.map(id => String(id)) : [],
        arbitrationSchemaType: arbitrationSchemaType || null,
        expenseType: arbitrationSchemaType === 'TAHKIM_BASVURU_UCRETI_ODEMESI' ? 'TAHKİM BAŞVURU ÜCRETİ ÖDEMESİ'
            : arbitrationSchemaType === 'BILIRKISI_ODEMESI' ? 'BİLİRKİŞİ ÖDEMESİ'
                : arbitrationSchemaType === 'ISLAH_ODEMESI' ? 'ISLAH ÖDEMESİ'
                    : arbitrationSchemaType === 'KARSI_VEKALET_ODEMESI' ? 'KARŞI VEKALET ÖDEMESİ'
                        : arbitrationSchemaType === 'ITIRAZ_ODEMESI' ? 'İTİRAZ ÖDEMESİ' : '',
    }), [relatedArbitrationSchema, arbitrationSchemaType, accidentFile, autoAssignCustomer, customerId, callformId, initialCustomerContractRate, initialIsMiddlemanContractBased, initialMiddlemanContractRate, initialMiddlemanContractPrice, defaultAmount]);

    const customHandlers = useMemo(() => ({
        isMiddlemanContractBased: (value, prev) => ({
            ...prev,
            isMiddlemanContractBased: value === 'true',
            middlemanContractRate: value === 'true' ? prev.middlemanContractRate : null,
            middlemanContractPrice: value !== 'true' ? prev.middlemanContractPrice : null,
        }),
        customerContractRate: (value, prev) => {
            const processed = normalizeFieldValue('customerContractRate', value);
            return { ...prev, customerContractRate: processed || '' };
        },
        middlemanContractRate: (value, prev) => {
            const processed = normalizeFieldValue('middlemanContractRate', value);
            return { ...prev, middlemanContractRate: processed || '' };
        },
        middlemanContractPrice: (value, prev) => {
            const processed = normalizeDecimal(value);
            return { ...prev, middlemanContractPrice: processed === '' ? null : processed };
        },
    }), []);

    const validationSchema = useMemo(() => ({
        amount: {
            when: () => !readOnlyView,
            required: true,
            message: 'Tutar zorunludur.',
            custom: (value) => {
                const numericValue = parseCurrencyToFloat(value);
                if (numericValue <= 0) return 'Geçerli pozitif bir tutar giriniz.';
                return null;
            },
        },
        date: {
            when: () => !readOnlyView,
            required: true,
            message: 'Tarih zorunludur.',
        },
        invoiceNumber: {
            when: () => !readOnlyView,
            custom: (value) => {
                if (!value) return null;
                if (!validatePositiveInteger(value)) return 'Fatura numarası sadece rakam içermelidir.';
                return null;
            },
        },
        description: {
            when: () => !readOnlyView,
            maxLength: 500,
            maxLengthMessage: 'Açıklama en fazla 500 karakter olabilir.',
        },
        customerContractRate: {
            when: () => !readOnlyView,
            min: 0,
            max: 100,
            rangeMessage: 'Müşteri sözleşme oranı 0-100 arasında olmalıdır.',
        },
        middlemanContractRate: {
            when: (fd) => !readOnlyView && fd.isMiddlemanContractBased === true,
            min: 0,
            max: 100,
            rangeMessage: 'Aracı sözleşme oranı 0-100 arasında olmalıdır.',
        },
        middlemanContractPrice: {
            when: (fd) => !readOnlyView && fd.isMiddlemanContractBased === false,
            custom: (value) => {
                if (value === null || value === undefined || value === '') return null;
                const price = parseCurrencyToFloat(value);
                if (price < 0) return 'Geçerli pozitif bir aracı sözleşme fiyatı giriniz.';
                return null;
            },
        },
    }), [readOnlyView]);

    const {
        formData: expenseData,
        errors,
        handleCancel: handleCrudCancel,
        handleInputChange,
        handleSubmit,
        isSubmitting,
    } = useModalCrudState({
        isOpen,
        mode: readOnlyView && selectedExpense ? 'modify' : mode,
        selectedItem: selectedExpense,
        initialData: initialExpenseData,
        toggle,
        mapSelectedToData: ({ initialData, selectedItem }) => {
            const formattedDate = toLocalISOString(selectedItem?.date || new Date());
            const bankId = Array.isArray(selectedItem?.associatedBank) && selectedItem.associatedBank.length > 0
                ? selectedItem.associatedBank[0]
                : selectedItem?.associatedBank?.id || '';
            const accidentFiles = Array.isArray(selectedItem?.accidentFile) ? selectedItem.accidentFile : [];
            const customers = autoAssignCustomer && customerId
                ? [customerId]
                : (Array.isArray(selectedItem?.customer) ? selectedItem.customer : []);
            const callForms = callformId
                ? [callformId]
                : (Array.isArray(selectedItem?.callForm) ? selectedItem.callForm : []);
            const relatedArbitrations = Array.isArray(selectedItem?.relatedArbitrationSchema) ? selectedItem.relatedArbitrationSchema : [];

            return {
                ...initialData,
                ...selectedItem,
                date: formattedDate,
                associatedBank: bankId,
                accidentFile: accidentFiles.map(id => String(id)),
                customer: customers.map(id => String(id)),
                callForm: callForms.map(id => String(id)),
                relatedArbitrationSchema: relatedArbitrations.map(id => String(id)),
                arbitrationSchemaType: selectedItem?.arbitrationSchemaType || null,
                amount: String(selectedItem?.amount || ''),
                invoiceNumber: String(selectedItem?.invoiceNumber || ''),
                expenseType: selectedItem?.expenseType || '',
                customerContractRate: selectedItem?.customerContractRate || null,
                isMiddlemanContractBased: selectedItem?.isMiddlemanContractBased === true,
                middlemanContractRate: selectedItem?.isMiddlemanContractBased === true ? (selectedItem?.middlemanContractRate || null) : null,
                middlemanContractPrice: selectedItem?.isMiddlemanContractBased === false ? (selectedItem?.middlemanContractPrice || null) : null,
            };
        },
        normalizeInput: true,
        customHandlers,
        validationSchema,
        submitConfig: {
            apiEndpoint,
            refreshData,
            componentName: 'Masraf',
            buildPayload: (data) => {
                const amountToSend = parseCurrencyToFloat(data.amount);
                const invoiceNumberToSend = data.invoiceNumber ? Number(data.invoiceNumber) : null;
                const dateToSend = data.date ? new Date(data.date).toISOString() : null;
                const bankIdToSend = data.associatedBank ? [String(data.associatedBank)] : [];
                const expenseTypeToSend = data.expenseType === '' ? null : data.expenseType;

                return {
                    expenseNumber: data.expenseNumber || null,
                    expenseType: expenseTypeToSend,
                    amount: amountToSend,
                    associatedBank: bankIdToSend,
                    date: dateToSend,
                    paymentStatus: 'Ödendi',
                    invoiceNumber: invoiceNumberToSend,
                    description: data.description || '',
                    accidentFile: Array.isArray(data.accidentFile) ? data.accidentFile.map(id => String(id)) : [],
                    customer: Array.isArray(data.customer) ? data.customer.map(id => String(id)) : [],
                    callForm: Array.isArray(data.callForm) ? data.callForm.map(id => String(id)) : [],
                    relatedArbitrationSchema: Array.isArray(data.relatedArbitrationSchema) ? data.relatedArbitrationSchema.map(id => String(id)) : [],
                    arbitrationSchemaType: data.arbitrationSchemaType || null,
                    customercontractrate: data.customerContractRate ? parseFloat(data.customerContractRate) : null,
                    ismiddlemancontractbased: data.isMiddlemanContractBased,
                    middlemancontractrate: data.isMiddlemanContractBased && data.middlemanContractRate ? parseFloat(data.middlemanContractRate) : null,
                    middlemancontractprice: !data.isMiddlemanContractBased && data.middlemanContractPrice ? parseCurrencyToFloat(data.middlemanContractPrice) : null,
                    ...(mode === 'modify' && selectedExpense?.id && { id: selectedExpense.id })
                };
            },
        },
    });

    const wrappedHandleInputChange = readOnlyView ? () => {} : handleInputChange;

    const [banks, setBanks] = useState([]);
    const bankApiUrl = process.env.REACT_APP_LACIVERT_API_ENDPOINT + '/bank';

    useEffect(() => {
        const fetchBanks = async () => {
            if (isOpen && bankApiUrl && !readOnlyView) {
                try {
                    await fetchTableData({
                        apiEndpoint: bankApiUrl,
                        page: 1,
                        pageSize: 1000,
                        setDataCallback: (apiResponse) => {
                            const items = apiResponse?.data?.items || apiResponse?.items || (Array.isArray(apiResponse) ? apiResponse : []);
                            setBanks(items);
                        },
                        setTotalRowsCallback: () => { }
                    });
                } catch (error) {
                    if (shouldToastError(error)) {
                        toast.error("Bankalar yüklenirken bir hata oluştu.");
                    }
                    setBanks([]);
                }
            }
        };
        fetchBanks();
    }, [isOpen, bankApiUrl, readOnlyView]);

    const handleCancel = () => {
        if (readOnlyView) {
            toggle();
            return;
        }
        handleCrudCancel();
    };

    return (
        <Modal isOpen={isOpen} toggle={handleCancel} backdrop="static">
            <ModalHeader toggle={handleCancel}>
                {readOnlyView ? 'Masraf Detayları' : (mode === 'add' ? 'Masraf Ekle' : 'Masraf Düzenle')}
            </ModalHeader>
            <ModalBody>
                <Form>
                    <FormGroup>
                        <Label for="expenseNumber">Masraf Numarası</Label>
                        <Input
                            type="text"
                            id="expenseNumber"
                            name="expenseNumber"
                            value={expenseData.expenseNumber || ''}
                            readOnly
                            disabled
                            placeholder="Sistem Tarafından Atanır"
                        />
                    </FormGroup>

                    <FormGroup>
                        <Label for="expenseType">Masraf Tipi {!readOnlyView && <span className="text-danger">*</span>}</Label>
                        <Input
                            type="select"
                            id="expenseType"
                            name="expenseType"
                            value={expenseData.expenseType || ''}
                            onChange={wrappedHandleInputChange}
                            invalid={!readOnlyView && !!errors.expenseType}
                            disabled={readOnlyView ||
                                (arbitrationSchemaType === 'TAHKIM_BASVURU_UCRETI_ODEMESI') ||
                                (arbitrationSchemaType === 'BILIRKISI_ODEMESI') ||
                                (arbitrationSchemaType === 'ISLAH_ODEMESI') ||
                                (arbitrationSchemaType === 'KARSI_VEKALET_ODEMESI') ||
                                (arbitrationSchemaType === 'ITIRAZ_ODEMESI')
                            }>
                            <option value="">Masraf Tipi Seçiniz</option>
                            <option value="NOTER ÜCRETİ">NOTER ÜCRETİ</option>
                            <option value="ARACI ÖDEMESİ">ARACI ÖDEMESİ</option>
                            <option value="SATIN ALMA BEDELİ">SATIN ALMA BEDELİ</option>
                            <option value="360 SORGU BEDELİ">360 SORGU BEDELİ</option>
                            <option value="EKSPER FATURASI">EKSPER FATURASI</option>
                            <option value="KEP BAŞVURU">KEP BAŞVURU</option>
                            <option value="TAHKİM HARCI">TAHKİM HARCI</option>
                            <option value="TAHKİM BAŞVURU ÜCRETİ ÖDEMESİ">TAHKİM BAŞVURU ÜCRETİ ÖDEMESİ</option>
                            <option value="BİLİRKİŞİ ÖDEMESİ">BİLİRKİŞİ ÖDEMESİ</option>
                            <option value="ISLAH ÖDEMESİ">ISLAH ÖDEMESİ</option>
                            <option value="İTİRAZ ÖDEMESİ">İTİRAZ ÖDEMESİ</option>
                            <option value="KARŞI VEKALET ÖDEMESİ">KARŞI VEKALET ÖDEMESİ</option>
                            <option value="BARO PULU">BARO PULU</option>
                            <option value="TAKİP AÇILIŞ MASRAFI">TAKİP AÇILIŞ MASRAFI</option>
                            <option value="HACİZ MASRAFI">HACİZ MASRAFI</option>
                            <option value="TAHSİL HARCI">TAHSİL HARCI</option>
                            <option value="CEZAEVİ HARCI">CEZAEVİ HARCI</option>
                        </Input>
                        {!readOnlyView && <FormFeedback>{errors.expenseType}</FormFeedback>}
                    </FormGroup>

                    <FormGroup>
                        <Label for="amount">Tutar {!readOnlyView && <span className="text-danger">*</span>}</Label>
                        <Input
                            type="text"
                            id="amount"
                            name="amount"
                            value={readOnlyView && expenseData.amount ? formatCurrency(expenseData.amount) : displayDecimal(expenseData.amount || '')}
                            onChange={wrappedHandleInputChange}
                            placeholder="Örn: 1000,50"
                            invalid={!readOnlyView && !!errors.amount}
                            readOnly={readOnlyView}
                        />
                        {!readOnlyView && <FormFeedback>{errors.amount}</FormFeedback>}
                    </FormGroup>

                    <FormGroup>
                        <Label for="associatedBank">İlgili Banka</Label>
                        <Input
                            type="select"
                            id="associatedBank"
                            name="associatedBank"
                            value={expenseData.associatedBank || ''}
                            onChange={wrappedHandleInputChange}
                            invalid={!readOnlyView && !!errors.associatedBank}
                            disabled={readOnlyView}
                        >
                            <option value="">Banka Seçiniz (Opsiyonel)</option>
                            {banks?.map(bank => (
                                <option key={bank.id} value={String(bank.id)}>
                                    {bank.bankName} {bank.branchName ? `- ${bank.branchName}` : ''}{bank.code ? '' : ` (IBAN: ${bank.iban})`}
                                </option>
                            ))}
                        </Input>
                        {!readOnlyView && <FormFeedback>{errors.associatedBank}</FormFeedback>}
                    </FormGroup>

                    <FormGroup>
                        <Label for="date">Tarih ve Saat {!readOnlyView && <span className="text-danger">*</span>}</Label>
                        <Input
                            type={readOnlyView ? "text" : "datetime-local"}
                            id="date"
                            name="date"
                            value={readOnlyView ? (expenseData.date ? new Date(expenseData.date).toLocaleString() : '') : (expenseData.date || '')}
                            onChange={wrappedHandleInputChange}
                            invalid={!readOnlyView && !!errors.date}
                            readOnly={readOnlyView}
                        />
                        {!readOnlyView && <FormFeedback>{errors.date}</FormFeedback>}
                    </FormGroup>

                    <FormGroup>
                        <Label for="invoiceNumber">Fatura Numarası</Label>
                        <Input
                            type="text"
                            id="invoiceNumber"
                            name="invoiceNumber"
                            value={expenseData.invoiceNumber || ''}
                            onChange={wrappedHandleInputChange}
                            placeholder="Fatura Numarası (Opsiyonel)"
                            invalid={!readOnlyView && !!errors.invoiceNumber}
                            readOnly={readOnlyView}
                        />
                        {!readOnlyView && <FormFeedback>{errors.invoiceNumber}</FormFeedback>}
                    </FormGroup>

                    {((callformId && mode === 'add') || (mode === 'modify' && expenseData.callForm && expenseData.callForm.length > 0)) && !readOnlyView && (expenseData.expenseType === 'ARACI ÖDEMESİ' || expenseData.expenseType === 'SATIN ALMA BEDELİ') && (
                        <>
                            <FormGroup>
                                <Label for="customerContractRate">Müşteri Sözleşme Oranı (%)</Label>
                                <Input
                                    type="text"
                                    id="customerContractRate"
                                    name="customerContractRate"
                                    value={displayDecimal(expenseData.customerContractRate || '')}
                                    onChange={wrappedHandleInputChange}
                                    placeholder="0-100"
                                    invalid={!!errors.customerContractRate}
                                    disabled={readOnlyView}
                                />
                                <FormFeedback>{errors.customerContractRate}</FormFeedback>
                            </FormGroup>

                            <FormGroup className="mb-3">
                                <Row className="align-items-center">
                                    <Col xl={4} md={6}>
                                        <Label className="text-nowrap mb-0">Aracı Ödeme Türü</Label>
                                    </Col>
                                    <Col xl={8} md={6} className='mt-md-0 mt-2'>
                                        <div className="d-flex">
                                            <div className="radio radio-primary me-3 d-flex align-items-center">
                                                <Input
                                                    name="isMiddlemanContractBased"
                                                    type="radio"
                                                    value="true"
                                                    id="middlemanRateBased"
                                                    checked={expenseData.isMiddlemanContractBased === true}
                                                    onChange={wrappedHandleInputChange}
                                                    disabled={readOnlyView}
                                                />
                                                <Label check for="middlemanRateBased" className="ms-1 mb-0">Oran Bazlı</Label>
                                            </div>
                                            <div className="radio radio-primary d-flex align-items-center">
                                                <Input
                                                    name="isMiddlemanContractBased"
                                                    type="radio"
                                                    value="false"
                                                    id="middlemanPriceBased"
                                                    checked={expenseData.isMiddlemanContractBased === false}
                                                    onChange={wrappedHandleInputChange}
                                                    disabled={readOnlyView}
                                                />
                                                <Label check for="middlemanPriceBased" className="ms-1 mb-0">Fiyat Bazlı</Label>
                                            </div>
                                        </div>
                                    </Col>
                                </Row>
                            </FormGroup>

                            <Row>
                                <Col md={12}>
                                    {expenseData.isMiddlemanContractBased === true ? (
                                        <FormGroup>
                                            <Label for="middlemanContractRate">Aracı Sözleşme Oranı (%)</Label>
                                            <Input
                                                type="text"
                                                id="middlemanContractRate"
                                                name="middlemanContractRate"
                                                value={displayDecimal(expenseData.middlemanContractRate || '')}
                                                onChange={wrappedHandleInputChange}
                                                placeholder="0-100"
                                                invalid={!!errors.middlemanContractRate}
                                                disabled={readOnlyView || expenseData.isMiddlemanContractBased === false}
                                            />
                                            <FormFeedback>{errors.middlemanContractRate}</FormFeedback>
                                        </FormGroup>
                                    ) : (
                                        <FormGroup>
                                            <Label for="middlemanContractPrice">Aracı Sözleşme Fiyatı (₺)</Label>
                                            <Input
                                                type="text"
                                                id="middlemanContractPrice"
                                                name="middlemanContractPrice"
                                                value={displayDecimal(expenseData.middlemanContractPrice || '')}
                                                onChange={wrappedHandleInputChange}
                                                placeholder="Örn: 1000,50"
                                                invalid={!!errors.middlemanContractPrice}
                                                disabled={readOnlyView || expenseData.isMiddlemanContractBased === true}
                                            />
                                            <FormFeedback>{errors.middlemanContractPrice}</FormFeedback>
                                        </FormGroup>
                                    )}
                                </Col>
                            </Row>
                        </>
                    )}
                    {((callformId && mode === 'add') || (mode === 'modify' && expenseData.callForm && expenseData.callForm.length > 0)) && readOnlyView && (expenseData.expenseType === 'ARACI ÖDEMESİ' || expenseData.expenseType === 'SATIN ALMA BEDELİ') && (
                        <>
                            <FormGroup>
                                <Label for="customerContractRate">Müşteri Sözleşme Oranı (%)</Label>
                                <Input type="text" value={expenseData.customerContractRate !== null ? `${displayDecimal(expenseData.customerContractRate)}%` : '-'} readOnly disabled />
                            </FormGroup>
                            <FormGroup>
                                <Label>Aracı Ödeme Türü</Label>
                                <Input type="text" value={expenseData.isMiddlemanContractBased ? 'Oran Bazlı' : 'Fiyat Bazlı'} readOnly disabled />
                            </FormGroup>
                            {expenseData.isMiddlemanContractBased ? (
                                <FormGroup>
                                    <Label for="middlemanContractRate">Aracı Sözleşme Oranı (%)</Label>
                                    <Input type="text" value={expenseData.middlemanContractRate !== null ? `${displayDecimal(expenseData.middlemanContractRate)}%` : '-'} readOnly disabled />
                                </FormGroup>
                            ) : (
                                <FormGroup>
                                    <Label for="middlemanContractPrice">Aracı Sözleşme Fiyatı (₺)</Label>
                                    <Input type="text" value={expenseData.middlemanContractPrice !== null ? formatCurrency(expenseData.middlemanContractPrice) : '-'} readOnly disabled />
                                </FormGroup>
                            )}
                        </>
                    )}

                    <FormGroup>
                        <Label for="description">Açıklama</Label>
                        <Input
                            type="textarea"
                            id="description"
                            name="description"
                            value={expenseData.description || ''}
                            onChange={wrappedHandleInputChange}
                            placeholder="Masrafla İlgili Ek Açıklamalar (Opsiyonel)"
                            rows="3"
                            invalid={!readOnlyView && !!errors.description}
                            readOnly={readOnlyView}
                        />
                        {!readOnlyView && <FormFeedback>{errors.description}</FormFeedback>}
                    </FormGroup>
                </Form>

                <hr className="my-4" />
                <h6 className="mb-3">Fatura</h6>
                {mode === 'modify' && selectedExpense?.id ? (
                    <FilesComponent
                        referenceId={selectedExpense.id}
                        callFormReference={false}
                        multiple={false}
                        canEdit={!readOnlyView}
                        typeFilter={["Fatura"]}
                        title=""
                        onFilesChange={() => { }}
                    />
                ) : (
                    <div className="text-muted small">Fatura eklemek için önce masrafı kaydedin.</div>
                )}
            </ModalBody>
            <ModalFooter>
                {readOnlyView ? (
                    <Button color="secondary" onClick={handleCancel}>
                        Kapat
                    </Button>
                ) : (
                    <>
                        <Button color="outline-success" onClick={handleSubmit} disabled={isSubmitting} className="rounded-pill">
                            {isSubmitting ? 'Kaydediliyor...' : (mode === 'add' ? 'Ekle' : 'Güncelle')}
                        </Button>
                        <Button color="outline-danger" onClick={handleCancel} disabled={isSubmitting} className="ms-2 rounded-pill">
                            İptal
                        </Button>
                    </>
                )}
            </ModalFooter>
        </Modal>
    );
};

ExpenseDataModal.propTypes = {
    apiEndpoint: PropTypes.string.isRequired,
    isOpen: PropTypes.bool.isRequired,
    toggle: PropTypes.func.isRequired,
    mode: PropTypes.oneOf(['add', 'modify']).isRequired,
    selectedExpense: PropTypes.object,
    refreshData: PropTypes.func.isRequired,
    customerId: PropTypes.string,
    autoAssignCustomer: PropTypes.bool,
    accidentFile: PropTypes.arrayOf(PropTypes.string),
    callformId: PropTypes.string,
    readOnlyView: PropTypes.bool,
    initialCustomerContractRate: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    initialIsMiddlemanContractBased: PropTypes.bool,
    initialMiddlemanContractRate: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    initialMiddlemanContractPrice: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    relatedArbitrationSchema: PropTypes.arrayOf(PropTypes.string),
    arbitrationSchemaType: PropTypes.string,
    defaultAmount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

ExpenseDataModal.defaultProps = {
    readOnlyView: false,
    initialCustomerContractRate: null,
    initialIsMiddlemanContractBased: false,
    initialMiddlemanContractRate: null,
    initialMiddlemanContractPrice: null,
    relatedArbitrationSchema: [],
    arbitrationSchemaType: null,
};

export default ExpenseDataModal;
